import axios from "axios";
import { Auth0Provider } from "@auth0/auth0-react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { frFR } from "@mui/material/locale";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SnackbarProvider } from "notistack";
import WebFont from "webfontloader";
import { JobsProvider } from "$hooks/useJobs";
import { store } from "$store";
import constants from "$utils/constants";
import { queryClient } from "$utils/queryClient";
import { SocketProvider } from "$utils/socket";
import { theme } from "$utils/theme/bataillog";
import "./index.css";
import Route, { router } from "./Route";
import MaintenancePage from "./maintenance/MaintenancePage";
import reportWebVitals from "./reportWebVitals";
import { GlobalSnackbarListener } from "$components/GlobalSnackbarListener";
import { initHyperDX } from "$utils/hyperdx";
import {
  handleUnauthorized,
  installFetchUnauthorizedGuard,
} from "$utils/unauthorizedSession";

const initializeSentry = () => {
  Sentry.init({
    dsn:
      process.env.NODE_ENV === "production"
        ? (process.env.SENTRY_DSN ??
          "https://d26f6a9e61e04ce4be503886396a0740@o4504560547069952.ingest.sentry.io/4504561953931264")
        : null,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    defaultIntegrations: [Sentry.replayIntegration()],
  });
};

const initializeAxios401Interceptor = () => {
  axios.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      const status = error?.response?.status ?? error?.status;
      if (status === 401) {
        const requestUrl = String(
          error?.request?.responseURL || error?.config?.url || ""
        );
        handleUnauthorized(requestUrl);
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );
};

const isMaintenanceModeEnabled = () => {
  const raw = (process.env.MAINTENANCE_MODE ?? "").toString().trim();
  return ["1", "true", "yes", "on"].includes(raw.toLowerCase());
};

const muiTheme = createTheme(
  {
    palette: {
      primary: {
        main: "#3f51b5",
        dark: "#303f9f",
        light: "#7986cb",
      },
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.8rem",
          },
          popper: {
            zIndex: 9999,
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          disableInjectingGlobalStyles: true,
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: theme.color.blueLight,
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontSize: 16,
          },
        },
      },
    },
    typography: {
      caption: undefined,
    },
  },
  frFR
);

WebFont.load({
  google: {
    families: [
      "Work Sans:700,600,500,400,300,200,100",
      "Roboto:700,600,500,400,300,200,100",
    ],
  },
});

const container = document.getElementById("root");

if (!container) {
  throw new Error('Root container with id "root" was not found');
}

const root = createRoot(container);

initializeSentry();
initializeAxios401Interceptor();
installFetchUnauthorizedGuard();
initHyperDX().catch((error) => {
  // eslint-disable-next-line no-console
  console.warn("HyperDX initialization failed", error);
});

if (isMaintenanceModeEnabled()) {
  root.render(<MaintenancePage />);
} else {
  const onRedirectCallback = (appState: any) => {
    router.navigate(
      appState && appState.returnTo
        ? appState.returnTo
        : window.location.pathname
    );
  };

  root.render(
    <ThemeProvider theme={muiTheme}>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <Provider store={store}>
            <SnackbarProvider>
              <GlobalSnackbarListener />
              <Auth0Provider
                domain={constants.AUTH0.DOMAIN}
                clientId={constants.AUTH0.CLIENTID}
                authorizationParams={{
                  audience: constants.AUTH0.AUDIENCE,
                  redirect_uri: window.location.origin,
                }}
                onRedirectCallback={onRedirectCallback}
              >
                <SocketProvider>
                  <JobsProvider>
                    <Route />
                  </JobsProvider>
                </SocketProvider>
              </Auth0Provider>
            </SnackbarProvider>
          </Provider>
        </QueryClientProvider>
      </ThemeProvider>
    </ThemeProvider>
  );
}

reportWebVitals();
